import $ from 'jquery';
import { getState } from '~src/state';
import { px2mm, px2cm, mm2px, cm2px } from '~src/utils/units';
import {
  RULER_SIZE,
  RULER_BG_COLOR,
  RULER_MARK_COLOR,
  RULER_TEXT_COLOR,
} from './constants';
import { getCurrentDPI } from './utils';

let horizontalRuler = null;
let verticalRuler = null;

export const createRulers = () => {
  const canvasWidth = window.canvas.getWidth();
  const canvasHeight = window.canvas.getHeight();

  // Create horizontal ruler container
  const $horizontalRuler = $('<canvas>')
    .attr('id', 'horizontal-ruler')
    .attr('width', canvasWidth)
    .attr('height', RULER_SIZE)
    .css({
      position: 'absolute',
      top: '-' + RULER_SIZE + 'px',
      left: '0',
      backgroundColor: RULER_BG_COLOR,
      cursor: 'ns-resize',
      display: 'none',
      zIndex: 1000,
      userSelect: 'none',
      '-webkit-user-select': 'none',
      '-moz-user-select': 'none',
    });

  // Create vertical ruler container
  const $verticalRuler = $('<canvas>')
    .attr('id', 'vertical-ruler')
    .attr('width', RULER_SIZE)
    .attr('height', canvasHeight)
    .css({
      position: 'absolute',
      top: '0',
      left: '-' + RULER_SIZE + 'px',
      backgroundColor: RULER_BG_COLOR,
      cursor: 'ew-resize',
      display: 'none',
      zIndex: 1000,
      userSelect: 'none',
      '-webkit-user-select': 'none',
      '-moz-user-select': 'none',
    });

  // Append to the main canvas container (parent of fabric's canvas-container)
  const $canvasWrapper = $('#main-canvas-container');
  $canvasWrapper.css('position', 'relative');
  $canvasWrapper.append($horizontalRuler).append($verticalRuler);

  horizontalRuler = $horizontalRuler[0].getContext('2d');
  verticalRuler = $verticalRuler[0].getContext('2d');
};

export const drawRulerMarks = (rulerUnit) => {
  if (!horizontalRuler || !verticalRuler) return;

  const zoom = getState('currentCanvasZoom', 1);
  const canvasWidth = window.canvas.getWidth();
  const canvasHeight = window.canvas.getHeight();
  const dpi = getCurrentDPI();

  // Calculate intervals based on unit
  let minorInterval, mediumInterval, majorInterval;

  if (rulerUnit === 'mm') {
    // 1mm in pixels
    const mmInPx = mm2px(1, dpi);
    minorInterval = mmInPx; // Every 1mm
    mediumInterval = mmInPx * 5; // Every 5mm
    majorInterval = mmInPx * 10; // Every 10mm (1cm)
  } else if (rulerUnit === 'cm') {
    // 1cm in pixels
    const cmInPx = cm2px(1, dpi);
    minorInterval = cmInPx / 10; // Every 1mm
    mediumInterval = cmInPx / 2; // Every 5mm
    majorInterval = cmInPx; // Every 1cm
  } else {
    // Pixels (default)
    minorInterval = 10;
    mediumInterval = 50;
    majorInterval = 100;
  }

  // Draw horizontal ruler marks
  horizontalRuler.clearRect(0, 0, canvasWidth, RULER_SIZE);
  horizontalRuler.fillStyle = RULER_BG_COLOR;
  horizontalRuler.fillRect(0, 0, canvasWidth, RULER_SIZE);

  horizontalRuler.strokeStyle = RULER_MARK_COLOR;
  horizontalRuler.fillStyle = RULER_TEXT_COLOR;
  horizontalRuler.font = '10px Arial';

  // Adjust interval density based on zoom to avoid overcrowding
  let skipMinor = false;
  if (zoom < 0.5 && rulerUnit !== 'px') {
    skipMinor = true; // Skip minor marks at low zoom for mm/cm
  }

  // Draw marks at calculated intervals
  for (let i = 0; i <= canvasWidth; i += minorInterval) {
    const pixelPos = Math.round(i);
    let markHeight = 5;

    // Check if this is a medium or major mark
    if (Math.abs(pixelPos % majorInterval) < 1) {
      markHeight = 15;
    } else if (Math.abs(pixelPos % mediumInterval) < 1) {
      markHeight = 10;
    } else if (skipMinor) {
      continue; // Skip minor marks at low zoom
    }

    horizontalRuler.beginPath();
    horizontalRuler.moveTo(pixelPos, RULER_SIZE);
    horizontalRuler.lineTo(pixelPos, RULER_SIZE - markHeight);
    horizontalRuler.stroke();

    // Draw labels for major marks
    if (markHeight === 15 && pixelPos > 0) {
      let labelValue;
      if (rulerUnit === 'mm') {
        labelValue = Math.round(px2mm(pixelPos, dpi));
      } else if (rulerUnit === 'cm') {
        labelValue = px2cm(pixelPos, dpi).toFixed(1);
      } else {
        labelValue = pixelPos;
      }
      horizontalRuler.fillText(labelValue.toString(), pixelPos - 10, 10);
    }
  }

  // Draw vertical ruler marks
  verticalRuler.clearRect(0, 0, RULER_SIZE, canvasHeight);
  verticalRuler.fillStyle = RULER_BG_COLOR;
  verticalRuler.fillRect(0, 0, RULER_SIZE, canvasHeight);

  verticalRuler.strokeStyle = RULER_MARK_COLOR;
  verticalRuler.fillStyle = RULER_TEXT_COLOR;
  verticalRuler.font = '10px Arial';

  // Draw marks at calculated intervals
  for (let i = 0; i <= canvasHeight; i += minorInterval) {
    const pixelPos = Math.round(i);
    let markWidth = 5;

    // Check if this is a medium or major mark
    if (Math.abs(pixelPos % majorInterval) < 1) {
      markWidth = 15;
    } else if (Math.abs(pixelPos % mediumInterval) < 1) {
      markWidth = 10;
    } else if (skipMinor) {
      continue; // Skip minor marks at low zoom
    }

    verticalRuler.beginPath();
    verticalRuler.moveTo(RULER_SIZE, pixelPos);
    verticalRuler.lineTo(RULER_SIZE - markWidth, pixelPos);
    verticalRuler.stroke();

    // Draw labels for major marks
    if (markWidth === 15 && pixelPos > 0) {
      let labelValue;
      if (rulerUnit === 'mm') {
        labelValue = Math.round(px2mm(pixelPos, dpi));
      } else if (rulerUnit === 'cm') {
        labelValue = px2cm(pixelPos, dpi).toFixed(1);
      } else {
        labelValue = pixelPos;
      }

      verticalRuler.save();
      verticalRuler.translate(10, pixelPos + 5);
      verticalRuler.rotate(-Math.PI / 2);
      verticalRuler.fillText(labelValue.toString(), 0, 0);
      verticalRuler.restore();
    }
  }
};

export const updateRulerPosition = () => {
  const zoom = getState('currentCanvasZoom', 1);

  // Update ruler positions based on canvas position and zoom
  $('#horizontal-ruler').css({
    transform: `scale(${zoom})`,
    transformOrigin: 'left top',
  });

  $('#vertical-ruler').css({
    transform: `scale(${zoom})`,
    transformOrigin: 'left top',
  });
};

export const updateRulerContexts = () => {
  horizontalRuler = $('#horizontal-ruler')[0]?.getContext('2d');
  verticalRuler = $('#vertical-ruler')[0]?.getContext('2d');
};

// This function should be called from the main after:render event
export const onAfterRender = (rulerEnabled) => {
  if (rulerEnabled) {
    updateRulerPosition();
  }
};
